import { BASE_DOMAIN, IDLE_TIMEOUT_MINUTES } from '@shared/constants/config';

/**
 * When the user last did something — the clock behind idle auto-logout.
 *
 * Stored in a cookie on the portal's base domain, not in memory or localStorage, so every open tab
 * reads the same value: the main portal host AND company subdomains (acme.<base>), which are
 * separate origins and do not share localStorage. They all share one Keycloak session, so an idle
 * tab must not sign out a user who is busy in another one. Falls back to this tab's own memory when
 * cookies are unavailable.
 *
 * Only real input counts as activity (see useIdleLogout). API calls, polling and timers never call
 * markActivity, so a page that keeps fetching does not keep an absent user signed in.
 *
 * Depends only on config, so shared UI (StripeField) can use it without pulling in the auth feature.
 */

const COOKIE_NAME = 'cp_last_activity';
// mousemove fires constantly; a few seconds of resolution is plenty against a 15-minute limit.
const WRITE_THROTTLE_MS = 5000;

export const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;

let memoryLastActivity = 0;

// `domain=.<base>` makes the cookie visible to every portal host; anywhere else (localhost) it is
// left host-only.
function cookieDomainAttribute() {
  const host = (globalThis.location?.hostname || '').toLowerCase();
  const base = (BASE_DOMAIN || '').toLowerCase();
  if (base && (host === base || host.endsWith(`.${base}`))) return `; domain=.${base}`;
  return '';
}

function readCookieValue() {
  if (typeof document === 'undefined' || !document.cookie) return 0;
  const match = new RegExp(`(?:^|; )${COOKIE_NAME}=(\\d+)`).exec(document.cookie);
  return match ? Number(match[1]) : 0;
}

function writeCookieValue(value) {
  if (typeof document === 'undefined') return;
  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${value}; path=/; SameSite=Lax${secure}${cookieDomainAttribute()}`;
}

/** Epoch ms of the most recent activity in any tab, or 0 when none was recorded yet. */
export function getLastActivity() {
  return Math.max(readCookieValue(), memoryLastActivity);
}

/**
 * Record user activity now. Throttled; pass `{ force: true }` to write regardless (sign-in,
 * "Stay signed in").
 * @param {{ force?: boolean }} [options]
 */
export function markActivity({ force = false } = {}) {
  const now = Date.now();
  const sinceLastWrite = now - memoryLastActivity;
  // A negative gap means the clock was set back; record activity rather than going silent until the
  // clock catches up with the old value.
  if (!force && sinceLastWrite >= 0 && sinceLastWrite < WRITE_THROTTLE_MS) return;
  memoryLastActivity = now;
  writeCookieValue(now);
}

/** Milliseconds since the last activity (0 when none was recorded yet). */
export function getIdleMs(now = Date.now()) {
  const last = getLastActivity();
  return last ? Math.max(0, now - last) : 0;
}

/** True once the user has been inactive for the full idle timeout. */
export function isIdle(now = Date.now()) {
  return getIdleMs(now) >= IDLE_TIMEOUT_MS;
}
